import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider, Spin, App as AntApp } from 'antd';
import { Login } from '@/pages/Login';
import { OTPVerify } from '@/pages/OTPVerify';
import { Dashboard } from '@/pages/Dashboard';
import { Analytics } from '@/pages/Analytics';
import { Users } from '@/pages/Users';
import { Staff } from '@/pages/Staff';
import { TransactionQueuePage } from '@/pages/TransactionQueue';
import { SubscriptionsPage } from '@/pages/Subscriptions';
import { CardRenewalsPage } from '@/pages/CardRenewals';
import { Referrals } from '@/pages/Referrals';
import { KYCCases } from '@/pages/KYCCases';
import { WalletBalances } from '@/pages/WalletBalances';
import { TradevestOnboardingLogPage } from '@/pages/TradevestOnboardingLog';
import { AppSettingsPage } from '@/pages/AppSettings';
import { NotificationsPage } from '@/pages/Notifications';
import { BroadcastsPage } from '@/pages/Broadcasts';
import { AssetManagementPage } from '@/pages/AssetManagement';
import { AssetDetailPage } from '@/pages/AssetDetail';
import { IssuersPage } from '@/pages/Issuers';
import { IssuerAssetsPage } from '@/pages/IssuerAssets';
import { BrandSpacePage } from '@/pages/BrandSpace';
import { ThreeDSChallengesPage } from '@/pages/ThreeDSChallenges';
import { LegalDocumentsPage } from '@/pages/LegalDocuments';
import FinAPIOversight from '@/pages/FinAPIOversight';
import TradevestOnboardingOversight from '@/pages/TradevestOnboardingOversight';
import { KYCManualReviewPage } from '@/pages/KYCManualReview';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminRoute } from '@/components/AdminRoute';
import { StagingBanner } from '@/components/StagingBanner';
import { useAuth } from '@/hooks/useAuth';
import deDE from 'antd/locale/de_DE';
import { BackofficeLayout } from '@/components/BackofficeLayout';
import { backofficeTheme } from '@/theme';
import { queryClient } from '@/queryClient';

const isStaging = import.meta.env.VITE_ENV === 'staging';

function AppContent() {
  const { isInitializing } = useAuth();

  if (isInitializing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/otp-verify" element={<OTPVerify />} />

        {/* Protected routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<BackofficeLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/users" element={<Users />} />
            <Route path="/kyc-cases/:userId" element={<KYCCases />} />
            <Route path="/wallet-balances/:userId" element={<WalletBalances />} />
            <Route path="/tradevest-log/:userId" element={<TradevestOnboardingLogPage />} />
            {/* Add more protected routes here */}
            {/* Admin-only routes stay inside the authenticated shell. */}
            <Route element={<AdminRoute />}>
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/staff" element={<Staff />} />
              <Route path="/transaction-queue" element={<TransactionQueuePage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/card-renewals" element={<CardRenewalsPage />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/app-settings" element={<AppSettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/broadcasts" element={<BroadcastsPage />} />
              <Route path="/assets" element={<AssetManagementPage />} />
              <Route path="/assets/new" element={<AssetDetailPage />} />
              <Route path="/assets/:id" element={<AssetDetailPage />} />
              <Route path="/issuers" element={<IssuersPage />} />
              <Route path="/issuers/:issuer" element={<IssuerAssetsPage />} />
              <Route path="/issuers/:issuer/brand" element={<BrandSpacePage />} />
              <Route path="/three-ds" element={<ThreeDSChallengesPage />} />
              <Route path="/legal-documents" element={<LegalDocumentsPage />} />
              <Route path="/finapi-oversight" element={<FinAPIOversight />} />
              <Route path="/kyc-manual-review" element={<KYCManualReviewPage />} />
              <Route path="/tradevest-onboarding" element={<TradevestOnboardingOversight />} />
              {/* Add more admin routes here */}
            </Route>
          </Route>
        </Route>

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={backofficeTheme} locale={deDE}>
        <AntApp>
          {isStaging && <StagingBanner />}
          <AppContent />
        </AntApp>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
